import { useEffect, useRef, useState, type FormEvent } from "react";
import { useNavigate, useSearchParams } from "react-router";
import {
  AuthActions,
  AuthField,
  AuthNotice,
  AuthPage,
  PasswordInput,
  SubmitButton,
  authKitTextKeys,
  showResult,
} from "../../auth/authKit";
import { acceptInvitation, previewInvitation, readSignedIn, signOut, type InvitationPreview } from "./accountApi";
import { useFocusHeading } from "./focusHeading";

/** Claves de texto de la pantalla (las que lee Invitacion/Aceptar.razor), el aviso de falla técnica y las del kit. */
export const invitationTextKeys = [
  ...authKitTextKeys,
  "ACCEPT_",
  "CP_PASSWORD_RULE",
  "CP_INVITE_USE_EXISTING",
  "CP_INVITE_CREATE_ACCOUNT",
  "CP_AUTH_DATA_REJECTED",
  "ERROR_TEXT",
  // IDN-10: el rol de la invitación, con los mismos nombres que la pantalla de Usuarios.
  "ORGROLE_",
  // IDN-10 (decisión 14): «Cerrar sesión», el mismo texto del menú de la cuenta.
  "ACCOUNT_SIGN_OUT",
];

/** El mismo enlace con otro modo de cuenta (NavigationManager.GetUriWithQueryParameter): no consume la invitación. */
function accountModeHref(href: string, isNewAccount: boolean): string {
  const url = new URL(href);
  url.searchParams.set("account", isNewAccount ? "existing" : "new");
  return url.toString();
}

/** Nombre del rol principal como en Usuarios (usersUi). */
const roleKey = (role: string | null) =>
  role === "ORG_OWNER" ? "ORGROLE_OWNER" : role === "ORG_ADMIN" ? "ORGROLE_ADMIN" : "ORGROLE_MEMBER";

/**
 * Aceptar una invitación (/invitacion/aceptar?token=&account=, Aceptar.razor) con una cuenta nueva o con una existente,
 * con las piezas del kit. El enlace solo cambia la presentación; al enviar manda el modo del formulario. Un enlace
 * vencido o desconocido pasa a «enlace inválido»; una contraseña corregible deja el formulario con los datos escritos
 * (la clave se vacía).
 *
 * IDN-10: con una cuenta existente se acepta solo con la sesión de esa cuenta. Sin sesión, la pantalla lleva al login
 * (que vuelve aquí); con la sesión de la cuenta invitada muestra empresa y rol y el botón Aceptar; con la de otra cuenta,
 * lo dice. El servidor decide el estado; la pantalla solo lo dibuja.
 *
 * IDN-10 (decisión 14): con una sesión iniciada (también la limitada a la invitación o la de otra cuenta) se ofrece
 * «Cerrar sesión» (botón secundario, C04), que cierra la sesión como el menú de la cuenta y vuelve a esta invitación.
 */
export function InvitationPage({ L }: { L: (key: string) => string }) {
  const [params] = useSearchParams();
  // Cada modo es una pantalla nueva (como una carga de la página): el formulario empieza vacío y el título se enfoca.
  return <InvitationForm key={params.get("account") ?? ""} L={L} />;
}

function InvitationForm({ L }: { L: (key: string) => string }) {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token");
  const [isNewAccount] = useState(() => params.get("account") !== "existing");
  const [form, setForm] = useState({ email: "", firstName: "", lastName: "" });
  const [done, setDone] = useState(false);
  // IDN-10: destino en la empresa recién aceptada, ya con sesión (null: el servidor no pudo iniciarla, va al login).
  const [enterHref, setEnterHref] = useState<string | null>(null);
  const [invalid, setInvalid] = useState(() => !token?.trim());
  const [error, setError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [sending, setSending] = useState(false);
  // IDN-10: estado de la cuenta existente (null mientras el servidor responde).
  const [preview, setPreview] = useState<InvitationPreview | null>(null);
  // IDN-10 (decisión 14): hay una sesión en este navegador (normal, limitada a la invitación o de otra cuenta).
  const [signedIn, setSignedIn] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const password = useRef<HTMLInputElement>(null);
  const busy = useRef(false);
  const title = L("ACCEPT_TITLE");
  useEffect(() => {
    document.title = title;
  }, [title]);
  useFocusHeading(true);
  useEffect(() => {
    let alive = true;
    // Primero la sesión (deja el token antiforgery que usa la consulta siguiente), después lo que decide el servidor.
    void readSignedIn().then(async (session) => {
      if (!alive) return;
      setSignedIn(session);
      if (isNewAccount || !token?.trim()) return;
      const result = await previewInvitation(token.trim());
      if (!alive) return;
      if (result.status === "invalid") setInvalid(true);
      setPreview(result);
    });
    return () => {
      alive = false;
    };
  }, [isNewAccount, token]);
  const set = (key: keyof typeof form) => (event: { target: { value: string } }) =>
    setForm((current) => ({ ...current, [key]: event.target.value }));

  /** Al modo «cuenta existente», que pregunta al servidor qué corresponde (iniciar sesión, aceptar u otra cuenta). */
  const toExistingAccount = () => {
    const url = new URL(accountModeHref(window.location.href, true));
    navigate(url.pathname + url.search);
    window.scrollTo(0, 0);
  };

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (busy.current) return;
    busy.current = true;
    setSending(true);
    try {
      const result = await acceptInvitation({
        token,
        isNewAccount,
        ...(isNewAccount ? form : { email: "", firstName: "", lastName: "" }),
        password: password.current?.value ?? "",
      });
      if (password.current) password.current.value = "";
      // IDN-10: el correo ya tiene cuenta (o la sesión cambió): sigue el modo «cuenta existente».
      if (result.status === "signIn" || result.status === "otherAccount") {
        if (isNewAccount) toExistingAccount();
        else
          setPreview({
            status: result.status,
            organizationName: null,
            roleCode: null,
            loginHref: result.next,
            messageKey: result.messageKey,
          });
        return;
      }
      const onPassword = isNewAccount && result.messageKey === "CP_PASSWORD_RULE";
      showResult(() => {
        if (result.status === "done") {
          setEnterHref(result.next?.startsWith("/") && !result.next.startsWith("//") ? result.next : null);
          setDone(true);
        } else if (result.status === "invalid") setInvalid(true);
        setPasswordError(onPassword ? L(result.messageKey!) : null);
        setError(result.messageKey && !onPassword ? L(result.messageKey) : null);
        setAttempt((value) => value + 1);
      }, onPassword);
    } finally {
      busy.current = false;
      setSending(false);
    }
  };

  /**
   * IDN-10 (decisión 14): cierra la sesión (POST /logout, revoca en Identity) y vuelve a esta misma invitación, ya sin
   * sesión, para entrar con la cuenta correcta. Si no se pudo cerrar, la pantalla queda igual con el aviso genérico.
   */
  const leave = async () => {
    if (busy.current) return;
    busy.current = true;
    setSigningOut(true);
    try {
      if (await signOut()) {
        window.location.assign(window.location.href);
        return;
      }
      showResult(() => {
        setError(L("ERROR_TEXT"));
        setAttempt((value) => value + 1);
      }, false);
    } finally {
      busy.current = false;
      setSigningOut(false);
    }
  };

  const signOutButton = signedIn ? (
    <button
      type="button"
      className="tm-btn secondary"
      aria-busy={signingOut ? "true" : undefined}
      onClick={() => void leave()}
    >
      {L("ACCOUNT_SIGN_OUT")}
    </button>
  ) : null;

  /** Avisos sin otra acción (otra cuenta, enlace inválido): con sesión, solo «Cerrar sesión». */
  const signOutOnly = signOutButton ? <AuthActions>{signOutButton}</AuthActions> : null;

  const modeLink = (
    <p className="auth-mode">
      <a
        className="tm-btn secondary"
        href={accountModeHref(window.location.href, isNewAccount)}
        onClick={(event) => {
          // Como la navegación mejorada del portal actual: cambia de modo sin recargar la página.
          if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
          event.preventDefault();
          const url = new URL(event.currentTarget.href);
          navigate(url.pathname + url.search);
          window.scrollTo(0, 0);
        }}
      >
        {isNewAccount ? L("CP_INVITE_USE_EXISTING") : L("CP_INVITE_CREATE_ACCOUNT")}
      </a>
    </p>
  );

  const errorNotice = error ? (
    <AuthNotice key={attempt} tone="error">
      {error}
    </AuthNotice>
  ) : null;

  /** IDN-10: modo «cuenta existente» según lo que decidió el servidor. */
  const existingAccount = () => {
    if (!preview) return null;
    if (preview.status === "signIn")
      return (
        <>
          <p className="auth-intro">{L("ACCEPT_SIGNIN_TEXT")}</p>
          {modeLink}
          <AuthActions>
            <a className="tm-btn primary" href={preview.loginHref ?? "/login"}>
              {L("ACCEPT_SIGNIN")}
            </a>
          </AuthActions>
        </>
      );
    if (preview.status === "otherAccount")
      return (
        <>
          {errorNotice}
          <AuthNotice tone="error">{L("ACCEPT_OTHER_ACCOUNT")}</AuthNotice>
          {signOutOnly}
        </>
      );
    if (preview.status !== "ready")
      return (
        <>
          <AuthNotice tone="error">{L(preview.messageKey ?? "ERROR_TEXT")}</AuthNotice>
          {signOutOnly}
        </>
      );
    return (
      <>
        {errorNotice}
        <p className="auth-intro">
          {L("ACCEPT_READY_TEXT")
            .replace("{0}", preview.organizationName ?? "")
            .replace("{1}", L(roleKey(preview.roleCode)))}
        </p>
        <form method="post" onSubmit={(event) => void submit(event)}>
          <input type="hidden" name="form.IsNewAccount" value="false" />
          <AuthActions secondary={signOutButton}>
            <SubmitButton sending={sending}>{L("ACCEPT_SUBMIT")}</SubmitButton>
          </AuthActions>
        </form>
      </>
    );
  };

  return (
    <AuthPage title={title}>
      {done ? (
        <>
          <AuthNotice tone="done">{L("ACCEPT_DONE_TITLE")}</AuthNotice>
          {/* IDN-10 (decisión del dueño): con la sesión ya iniciada, «Entrar a la empresa» lleva a la empresa aceptada. */}
          <p className="auth-intro">{L(enterHref ? "ACCEPT_DONE_ENTER_TEXT" : "ACCEPT_DONE_TEXT")}</p>
          <AuthActions>
            <a className="tm-btn primary" href={enterHref ?? "/login"}>
              {L(enterHref ? "ACCEPT_ENTER" : "ACCEPT_LOGIN")}
            </a>
          </AuthActions>
        </>
      ) : invalid ? (
        <>
          {errorNotice}
          <AuthNotice tone="error">{L("ACCEPT_INVALID")}</AuthNotice>
          {signOutOnly}
        </>
      ) : !isNewAccount ? (
        existingAccount()
      ) : (
        <>
          {errorNotice}
          <p className="auth-intro">{L("ACCEPT_SUBTITLE_NEW")}</p>
          {modeLink}
          <form method="post" onSubmit={(event) => void submit(event)}>
            <input type="hidden" name="form.IsNewAccount" value="true" />
            <AuthField id="email" label={L("ACCEPT_EMAIL")}>
              <input
                id="email"
                type="email"
                autoComplete="email"
                required
                name="form.Email"
                value={form.email}
                onChange={set("email")}
              />
            </AuthField>
            <div className="tm-fields">
              <AuthField id="firstname" label={L("ACCEPT_FIRSTNAME")}>
                <input
                  id="firstname"
                  autoComplete="given-name"
                  name="form.FirstName"
                  value={form.firstName}
                  onChange={set("firstName")}
                />
              </AuthField>
              <AuthField id="lastname" label={L("ACCEPT_LASTNAME")}>
                <input
                  id="lastname"
                  autoComplete="family-name"
                  name="form.LastName"
                  value={form.lastName}
                  onChange={set("lastName")}
                />
              </AuthField>
            </div>
            <AuthField id="password" label={L("ACCEPT_PASSWORD")} error={passwordError} help={L("CP_PASSWORD_RULE")}>
              <PasswordInput
                id="password"
                name="form.Password"
                autoComplete="new-password"
                inputRef={password}
                error={passwordError}
                help={L("CP_PASSWORD_RULE")}
                L={L}
              />
            </AuthField>
            <AuthActions secondary={signOutButton}>
              <SubmitButton sending={sending}>{L("ACCEPT_SUBMIT")}</SubmitButton>
            </AuthActions>
          </form>
        </>
      )}
    </AuthPage>
  );
}
